import React, { useState, useCallback, useEffect } from "react";
import {
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  PencilIcon,
  BookmarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { TaskFormElement, TaskFormData } from "../../types";
import toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

interface TaskFormBuilderCompactProps {
  initialData?: TaskFormData;
  onSave?: (formData: TaskFormData) => void;
  onSaveAsTemplate?: (templateData: {
    title: string;
    description?: string;
    formStructure: {
      elements: TaskFormElement[];
      elementOrder: string[];
    };
    submissionData?: Record<string, any>;
  }) => void;
  onCancel?: () => void;
  isEditMode?: boolean;
  readonly?: boolean;
}

// AVAILABLE_FORM_ELEMENTS will be created dynamically with translations

export const TaskFormBuilderCompact: React.FC<TaskFormBuilderCompactProps> = ({
  initialData,
  onSave,
  onSaveAsTemplate,
  onCancel,
  isEditMode = false,
  readonly = false,
}) => {
  const { t } = useTranslation(["forms", "common"]);

  // Create AVAILABLE_FORM_ELEMENTS dynamically with translations
  const AVAILABLE_FORM_ELEMENTS: Omit<TaskFormElement, "id">[] = React.useMemo(
    () => [
      {
        type: "text",
        label: t("forms:elementLabels.textInput"),
        required: false,
        placeholder: t("forms:elementPlaceholders.enterText"),
      },
      {
        type: "textarea",
        label: t("forms:elementLabels.textarea"),
        required: false,
        placeholder: t("forms:elementPlaceholders.enterDescription"),
      },
      {
        type: "number",
        label: t("forms:elementLabels.numberInput"),
        required: false,
        placeholder: t("forms:elementPlaceholders.enterNumber"),
      },
      {
        type: "date",
        label: t("forms:elementLabels.datePicker"),
        required: false,
      },
      {
        type: "select",
        label: t("forms:elementLabels.selectList"),
        required: false,
        options: [
          t("forms:taskBuilder.option1"),
          t("forms:taskBuilder.option2"),
          t("forms:taskBuilder.option3"),
        ],
      },
      {
        type: "checkbox",
        label: t("forms:elementLabels.checkbox"),
        required: false,
      },
    ],
    [t]
  );

  const [formName, setFormName] = useState(initialData?.formName || "");
  const [formDescription, setFormDescription] = useState(
    initialData?.formDescription || ""
  );
  const [selectedElements, setSelectedElements] = useState<TaskFormElement[]>(
    () => {
      if (initialData?.elements && initialData?.elementOrder) {
        try {
          const orderArray = JSON.parse(initialData.elementOrder);
          return orderArray
            .map((id: string) =>
              initialData.elements.find((el) => el.id === id)
            )
            .filter(Boolean) as TaskFormElement[];
        } catch (e) {
          return initialData.elements;
        }
      }
      return [];
    }
  );
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [formSubmissionData, setFormSubmissionData] = useState<
    Record<string, any>
  >(initialData?.submissionData || {});
  const [expandedElementId, setExpandedElementId] = useState<string | null>(
    null
  );
  const [isToolbarMinimized, setIsToolbarMinimized] = useState(false);
  const supportsMultipleSelection = useCallback(
    (element: TaskFormElement) => element.type === "select",
    [],
  );
  const isMultipleSelectionEnabled = useCallback(
    (element: TaskFormElement) =>
      supportsMultipleSelection(element) && Boolean(element.allowMultiple),
    [supportsMultipleSelection],
  );

  const generateId = () =>
    `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Auto-save when form data changes
  React.useEffect(() => {
    if (
      !readonly &&
      onSave &&
      (formName || formDescription || selectedElements.length > 0)
    ) {
      // Debounce auto-save to avoid too many calls
      const timeoutId = setTimeout(() => {
        // Validate form title before saving (silently - no error toast)
        if (!formName.trim()) {
          return; // Skip save if no title, but don't show error
        }

        const formData: TaskFormData = {
          id: initialData?.id,
          formName: formName.trim(),
          formDescription: formDescription.trim(),
          elements: selectedElements,
          elementOrder: JSON.stringify(selectedElements.map((el) => el.id)),
          submissionData: {}, // Manager cannot enter values, form will be filled by users
          isTemplate: true,
        };

        onSave(formData);
      }, 1000); // Save after 1 second of no changes

      return () => clearTimeout(timeoutId);
    }
  }, [
    formName,
    formDescription,
    selectedElements,
    formSubmissionData,
    onSave,
    readonly,
    initialData?.id,
  ]);

  const addElement = useCallback((elementType: Omit<TaskFormElement, "id">) => {
    const newElement: TaskFormElement = {
      ...elementType,
      id: generateId(),
    };
    setSelectedElements((prev) => [...prev, newElement]);
    setExpandedElementId(newElement.id); // Auto-expand the new element
  }, []);

  const removeElement = useCallback((elementId: string) => {
    setSelectedElements((prev) => prev.filter((el) => el.id !== elementId));
  }, []);

  const moveElement = useCallback((fromIndex: number, toIndex: number) => {
    setSelectedElements((prev) => {
      const newElements = [...prev];
      const [movedElement] = newElements.splice(fromIndex, 1);
      newElements.splice(toIndex, 0, movedElement);
      return newElements;
    });
  }, []);

  const updateElement = useCallback(
    (elementId: string, updates: Partial<TaskFormElement>) => {
      setSelectedElements((prev) =>
        prev.map((el) => (el.id === elementId ? { ...el, ...updates } : el))
      );
    },
    []
  );

  const updateFormSubmissionData = useCallback(
    (fieldId: string, value: any) => {
      setFormSubmissionData((prev) => {
        const newData = {
          ...prev,
          [fieldId]: value,
        };
        return newData;
      });
    },
    []
  );

  const handleSave = useCallback(() => {
    if (!formName.trim()) {
      toast.error(t("forms:formNameRequired"));
      return;
    }

    if (selectedElements.length === 0) {
      toast.error(t("forms:atLeastOneElementRequired"));
      return;
    }

    // ✅ CRITICAL FIX: Create stable element IDs mapping for submission data
    // Map submission data to element IDs that are consistent with the element order
    const mappedSubmissionData: Record<string, any> = {};

    selectedElements.forEach((element, index) => {
      // Use element.id as key (whether negative temp ID or positive real ID)
      const elementId = element.id;

      if (formSubmissionData[elementId] !== undefined) {
        mappedSubmissionData[elementId] = formSubmissionData[elementId];
      }
    });

    const formData: TaskFormData = {
      id: initialData?.id,
      formName: formName.trim(),
      formDescription: formDescription.trim(),
      elements: selectedElements,
      elementOrder: JSON.stringify(selectedElements.map((el) => el.id)),
      submissionData: mappedSubmissionData, // Use mapped submission data
      isTemplate: true,
    };

    onSave?.(formData);
  }, [
    formName,
    formDescription,
    selectedElements,
    formSubmissionData,
    initialData?.id,
    onSave,
    t,
  ]);

  // ✅ Auto-save when submission data changes
  useEffect(() => {
    // Skip auto-save on initial render or if no elements
    if (selectedElements.length === 0 || !formName.trim()) {
      return;
    }

    // Only auto-save if there's actual submission data
    if (Object.keys(formSubmissionData).length > 0) {
      handleSave();
    } else {
    }
  }, [formSubmissionData, handleSave, selectedElements.length, formName]);

  const handleSaveAsTemplate = useCallback(() => {
    if (!formName.trim()) {
      toast.error(t("forms:formNameRequired"));
      return;
    }

    if (selectedElements.length === 0) {
      toast.error(t("forms:taskBuilder.templateMustContainElements"));
      return;
    }

    const templateData = {
      title: formName.trim(),
      description: formDescription.trim() || undefined,
      formStructure: {
        elements: selectedElements,
        elementOrder: selectedElements.map((el) => el.id),
      },
      submissionData: {}, // Manager cannot enter values, form will be filled by users
    };

    onSaveAsTemplate?.(templateData);
    toast.success(
      t("forms:taskBuilder.templateSavedSuccessfully", {
        name: formName.trim(),
      })
    );
  }, [
    formName,
    formDescription,
    selectedElements,
    formSubmissionData,
    onSaveAsTemplate,
    t,
  ]);

  const renderFormElement = (element: TaskFormElement, index: number) => {
    const value = formSubmissionData[element.id] || element.value || "";

    return (
      <div key={element.id} className="bg-gray-50 border rounded p-2 mb-2">
        {!readonly && (
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-medium flex-shrink-0">
                {element.type.toUpperCase()}
              </span>
              <div className="flex items-center space-x-1 flex-1 border border-gray-300 rounded-md px-2 py-1 bg-gray-50 hover:bg-white hover:border-blue-400 transition-all">
                <PencilIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={element.label}
                  onChange={(e) =>
                    updateElement(element.id, { label: e.target.value })
                  }
                  className="flex-1 text-xs font-medium bg-transparent border-none focus:outline-none min-w-0"
                  placeholder={t("forms:enterElementName")}
                />
              </div>
            </div>
            <div className="flex items-center space-x-1 flex-shrink-0">
              {index > 0 && (
                <button
                  onClick={() => moveElement(index, index - 1)}
                  className="p-1 text-gray-400 hover:text-blue-600 rounded"
                >
                  <ArrowUpIcon className="h-3 w-3" />
                </button>
              )}
              {index < selectedElements.length - 1 && (
                <button
                  onClick={() => moveElement(index, index + 1)}
                  className="p-1 text-gray-400 hover:text-blue-600 rounded"
                >
                  <ArrowDownIcon className="h-3 w-3" />
                </button>
              )}
              <button
                onClick={() => removeElement(element.id)}
                className="p-1 text-gray-400 hover:text-red-600 rounded"
              >
                <TrashIcon className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}

        {/* Form Element Rendering */}
        {element.type === "text" && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              {element.label}
            </label>
            <div className="w-full px-2 py-1 border-2 border-dashed border-gray-300 rounded bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-xs py-0.5">
                <DocumentTextIcon className="w-3 h-3 mr-1" />
                {t("forms:taskBuilder.willBeFilledByUser")}
              </div>
            </div>
          </div>
        )}

        {element.type === "textarea" && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              {element.label}
            </label>
            <div className="w-full px-2 py-1 border-2 border-dashed border-gray-300 rounded bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-xs py-3">
                <DocumentTextIcon className="w-3 h-3 mr-1" />
                {t("forms:taskBuilder.willBeFilledByUserMultiline")}
              </div>
            </div>
          </div>
        )}

        {element.type === "number" && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              {element.label}
            </label>
            <div className="w-full px-2 py-1 border-2 border-dashed border-gray-300 rounded bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-xs py-0.5">
                <DocumentTextIcon className="w-3 h-3 mr-1" />
                {t("forms:taskBuilder.willBeFilledByUserNumber")}
              </div>
            </div>
          </div>
        )}

        {element.type === "date" && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              {element.label}
            </label>
            <div className="w-full px-2 py-1 border-2 border-dashed border-gray-300 rounded bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-xs py-0.5">
                <DocumentTextIcon className="w-3 h-3 mr-1" />
                {t("forms:taskBuilder.willBeFilledByUserDate")}
              </div>
            </div>
          </div>
        )}

        {element.type === "select" && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              {element.label}
            </label>

            {/* Seçenek Düzenleme Paneli (Sadece edit modunda) */}
            {!readonly && (
              <div className="mb-2 p-2 bg-gray-50 rounded space-y-2">
                {supportsMultipleSelection(element) && (
                  <div className="flex items-center justify-between gap-3 rounded border border-blue-100 bg-white p-2">
                    <div>
                      <p className="text-xs font-medium text-gray-800">
                        {t("forms:projectBuilder.selectionMode")}
                      </p>
                      <p className="text-[11px] text-gray-600">
                        {t(
                          "forms:projectBuilder.selectionModeDescription",
                          "Bu alanda tekli veya çoklu seçim yapılmasını belirleyin.",
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[11px] font-semibold ${
                          !isMultipleSelectionEnabled(element)
                            ? "text-blue-700"
                            : "text-gray-500"
                        }`}
                      >
                        {t("forms:projectBuilder.singleSelection", "Tekli seçim")}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          updateElement(element.id, {
                            allowMultiple: !isMultipleSelectionEnabled(element),
                          })
                        }
                        className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                          isMultipleSelectionEnabled(element)
                            ? "bg-blue-600"
                            : "bg-gray-300"
                        }`}
                        aria-pressed={isMultipleSelectionEnabled(element)}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            isMultipleSelectionEnabled(element)
                              ? "translate-x-5"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                      <span
                        className={`text-[11px] font-semibold ${
                          isMultipleSelectionEnabled(element)
                            ? "text-blue-700"
                            : "text-gray-500"
                        }`}
                      >
                        {t("forms:projectBuilder.multipleSelection", "Çoklu seçim")}
                      </span>
                    </div>
                  </div>
                )}
                <label className="block text-xs font-medium text-gray-600">
                  {t("forms:options")}
                </label>

                {/* Seçenek Listesi */}
                <div className="space-y-1">
                  {(element.options || []).map((option, optionIndex) => (
                    <div key={optionIndex} className="flex items-center gap-1">
                      {/* Input Field */}
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...(element.options || [])];
                          newOptions[optionIndex] = e.target.value;
                          updateElement(element.id, { options: newOptions });
                        }}
                        placeholder={`${t("forms:option1")} ${optionIndex + 1}`}
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                      />

                      {/* Sil Butonu */}
                      <button
                        type="button"
                        onClick={() => {
                          const newOptions = (element.options || []).filter(
                            (_, i) => i !== optionIndex
                          );
                          // En az 1 seçenek olmalı
                          if (newOptions.length === 0) {
                            toast.error(t("forms:atLeastOneOptionRequired"));
                            return;
                          }
                          updateElement(element.id, { options: newOptions });
                        }}
                        disabled={(element.options || []).length <= 1}
                        className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title={t("forms:removeOption")}
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Seçenek Ekle Butonu */}
                <button
                  type="button"
                  onClick={() => {
                    const newOptions = [
                      ...(element.options || []),
                      `${t("forms:option1")} ${(element.options || []).length + 1}`,
                    ];
                    updateElement(element.id, { options: newOptions });
                  }}
                  className="w-full flex items-center justify-center gap-1 px-2 py-1 text-xs text-blue-600 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                >
                  <PlusIcon className="w-3 h-3" />
                  {t("forms:addOption")}
                </button>
              </div>
            )}

            {/* Preview / Read-only gösterim */}
            <div className="w-full px-2 py-1 border-2 border-dashed border-gray-300 rounded bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-xs py-0.5">
                <DocumentTextIcon className="w-3 h-3 mr-1" />
                {t("forms:taskBuilder.willBeFilledByUserSelect")}
              </div>
              <div className="mt-1 text-[11px] text-gray-400 text-center">
                {isMultipleSelectionEnabled(element)
                  ? t("forms:projectBuilder.multipleSelection", "Çoklu seçim")
                  : t("forms:projectBuilder.singleSelection", "Tekli seçim")}
              </div>
              {element.options && element.options.length > 0 && (
                <div className="mt-1 text-xs text-gray-400 text-center">
                  {t("forms:taskBuilder.options")}: {element.options.slice(0, 3).join(", ")}
                  {element.options.length > 3 && "..."}
                </div>
              )}
            </div>
          </div>
        )}

        {element.type === "checkbox" && (
          <div>
            <div className="w-full px-2 py-1 border-2 border-dashed border-gray-300 rounded bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-xs py-0.5">
                <DocumentTextIcon className="w-3 h-3 mr-1" />
                {t("forms:taskBuilder.willBeFilledByUserCheckbox")}
              </div>
              <div className="mt-0.5 text-xs text-gray-400 text-center">
                {element.label}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full bg-white flex flex-col">
      {/* Header */}
      <div className="p-2 border-b bg-gray-50">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1 mr-2">
            <div className="border-2 border-blue-200 rounded-lg px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 hover:border-blue-400 transition-all mb-2">
              <div className="flex items-center space-x-2 mb-1">
                <DocumentTextIcon className="w-4 h-4 text-blue-500" />
                <label className="text-xs font-medium text-gray-600">
                  {t("forms:taskBuilder.formName")} *
                </label>
              </div>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={t("forms:taskBuilder.enterFormName")}
                className="text-base font-bold bg-transparent border-none focus:outline-none w-full p-0 text-gray-800 placeholder-gray-400"
                disabled={readonly}
              />
            </div>
            <div className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 hover:border-gray-400 transition-all">
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                {t("forms:taskBuilder.descriptionOptional")}
              </label>
              <textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder={t("forms:taskBuilder.enterFormDescription")}
                className="text-xs text-gray-700 bg-transparent border-none focus:outline-none w-full p-0 resize-none placeholder-gray-400"
                rows={1}
                disabled={readonly}
              />
            </div>
          </div>
          <div className="flex space-x-1 flex-shrink-0">
            {onSaveAsTemplate && !readonly && (
              <button
                onClick={handleSaveAsTemplate}
                disabled={!formName.trim() || selectedElements.length === 0}
                className="flex items-center px-2 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title={t("forms:taskBuilder.saveAsTemplate")}
              >
                <BookmarkIcon className="h-3 w-3 mr-1" />
                {t("forms:template")}
              </button>
            )}
            {onCancel && (
              <button
                onClick={onCancel}
                className="px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
              >
                {t("common:cancel")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Element Palette */}
        {!readonly && (
          <div className="w-40 border-r border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0">
            <div className="p-2">
              <h4 className="text-xs font-semibold text-gray-900 mb-2">
                {t("forms:taskBuilder.elements")}
              </h4>
              <div className="space-y-1">
                {AVAILABLE_FORM_ELEMENTS.map((element, index) => (
                  <button
                    key={index}
                    onClick={() => addElement(element)}
                    className="w-full text-left px-2 py-2 bg-white hover:bg-blue-50 rounded text-xs border border-gray-200 hover:border-blue-300 transition-colors group"
                  >
                    <div className="font-medium text-gray-700 group-hover:text-blue-700 text-xs">
                      {element.label}
                    </div>
                    <div className="text-xs text-gray-400 uppercase mt-0.5">
                      {element.type}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Form Builder Area */}
        <div className="flex-1 overflow-y-auto p-2">
          {selectedElements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-5xl mb-4">📝</div>
              <h3 className="text-lg font-medium mb-2">
                {t("forms:taskBuilder.startCreatingForm")}
              </h3>
              <p className="text-sm mb-4">
                {readonly
                  ? t("forms:projectBuilder.noElementsInForm")
                  : t("forms:taskBuilder.selectFromLeftList")}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedElements.map((element, index) =>
                renderFormElement(element, index)
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
